/**
 * Compound-row grouping helpers for the Inspector's dynamic-block
 * section. Extracted from `InspectorSidebar.tsx` so the component file
 * only exports components (avoids the `react-refresh/only-export-components`
 * lint warning while keeping the HMR contract intact).
 *
 * AutoCAD's Properties palette renders polar / xy / point / basePoint
 * parameters as ONE row with two sub-fields — these helpers group the
 * corresponding suffixed `<base>.distance + <base>.angle` /
 * `<base>.x + <base>.y` scalar rows from `listDynamicParameters` into a
 * single compound descriptor before `InspectorSidebar` renders.
 *
 * Grouping requires the block's typed dynamicMetadata to disambiguate
 * the kind (point vs xy vs basePoint all use `.x`/`.y`). Without that
 * the helpers return a plain passthrough — the compound widening is
 * strictly ADDITIVE over the Variant A scalar-row behaviour.
 */

import type { DynListLine } from "@dxf-ai-editor/command-engine";
import type { BlockDefinition } from "@dxf-ai-editor/drawing-model";

import type { DynControlKind, DynNumericBounds } from "./dynamicBlockControls";

type ScalarRow = Extract<DynListLine, { kind: "param" }>;

type CompoundKindAlias = "polar" | "xy" | "point" | "basePoint";

/**
 * Unified row shape the Inspector JSX map renders. Either a compound
 * grouping (two suffixed scalars merged) or a plain scalar passthrough.
 */
type RenderRow =
  | {
      compound: true;
      kind: CompoundKindAlias;
      base: string;
      rawA: string;
      rawB: string;
    }
  | { compound: false; line: ScalarRow };

/**
 * Pair of `(<suffixA>, <suffixB>)` customProperties keys used by the
 * evaluator for a given compound kind (see
 * `applyInstanceParameters` in drawing-model/dynamicBlockMetadata.ts).
 */
export function compoundSuffixes(
  kind: DynControlKind,
): readonly [string, string] {
  if (kind === "polar") return [".distance", ".angle"];
  // xy / point / basePoint all use (.x, .y). Other kinds never hit
  // this codepath (DynParamControl routes them to scalar controls).
  return [".x", ".y"];
}

/**
 * Build a `name → kind` map for the block's compound parameters (polar
 * / xy / point / basepoint). Empty when there is no typed metadata —
 * compound grouping simply does not happen in that case (the suffixed
 * scalars render as individual rows, matching the pre-widening
 * fallback).
 */
function collectCompoundParamKinds(
  blockDef: BlockDefinition | undefined,
): Map<string, CompoundKindAlias> {
  const out = new Map<string, CompoundKindAlias>();
  const params = blockDef?.dynamicMetadata?.parameters ?? [];
  for (const p of params) {
    if (p.kind === "polar") out.set(p.name, "polar");
    else if (p.kind === "xy") out.set(p.name, "xy");
    else if (p.kind === "point") out.set(p.name, "point");
    else if (p.kind === "basepoint") out.set(p.name, "basePoint");
  }
  return out;
}

/**
 * Walk the scalar DynListLine list and group suffix pairs into
 * compound rows when:
 *   (a) both `<base>.<suffixA>` and `<base>.<suffixB>` are present, and
 *   (b) the block metadata declares `<base>` as a compound kind.
 *
 * Unmatched scalars (singleton halves, or pairs without typed kind)
 * fall through as plain ScalarRow entries so the Inspector never drops
 * a parameter the user can see via DYNLIST.
 *
 * Order of compound rows is pinned to the FIRST occurrence of either
 * suffix in the input — the scalar ordering from listDynamicParameters
 * is thus preserved for round-trip stability.
 */
export function collectRenderRows(
  lines: readonly ScalarRow[],
  blockDef: BlockDefinition | undefined,
): RenderRow[] {
  const kindMap = collectCompoundParamKinds(blockDef);
  if (kindMap.size === 0) {
    return lines.map((line) => ({ compound: false, line }) as const);
  }
  interface PartialPair {
    kind: CompoundKindAlias;
    a?: string;
    b?: string;
  }
  const partials = new Map<string, PartialPair>();
  const out: RenderRow[] = [];
  const placeholderIndexByBase = new Map<string, number>();

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]!;
    const suffixMatch = matchCompoundSuffix(line.name, kindMap);
    if (suffixMatch) {
      const { base, kind, slot } = suffixMatch;
      let partial = partials.get(base);
      if (!partial) {
        partial = { kind };
        partials.set(base, partial);
        // Reserve the slot in the render order at the first-occurrence
        // index so the compound row lands where the user expects.
        placeholderIndexByBase.set(base, out.length);
        out.push({
          compound: true,
          kind,
          base,
          rawA: "",
          rawB: "",
        });
      }
      if (slot === "a") partial.a = String(line.value);
      else partial.b = String(line.value);
      continue;
    }
    out.push({ compound: false, line });
  }
  // Finalize compound rows — fill rawA/rawB from the partial pairs.
  for (const [base, partial] of partials) {
    const idx = placeholderIndexByBase.get(base);
    if (idx === undefined) continue;
    const row = out[idx];
    if (!row || !row.compound) continue;
    out[idx] = {
      compound: true,
      kind: partial.kind,
      base,
      rawA: partial.a ?? "",
      rawB: partial.b ?? "",
    };
  }
  return out;
}

/**
 * Extract numeric bounds (valueSet) from the block's typed metadata
 * for a given parameter name — drives the slider's range + snap step
 * in `dynamicBlockControls`. Returns undefined when the block has no
 * metadata or the parameter is absent / non-numeric (flip / visibility
 * / lookup).
 */
export function boundsForParam(
  blockDef: BlockDefinition | undefined,
  name: string,
): DynNumericBounds | undefined {
  const params = blockDef?.dynamicMetadata?.parameters ?? [];
  for (const p of params) {
    if (p.name !== name) continue;
    if (p.kind === "linear") {
      const vs = p.valueSet;
      return normaliseBounds(vs?.mode, vs?.min, vs?.max, vs?.increment, vs?.listValues);
    }
    if (p.kind === "rotation") {
      const vs = p.angleValueSet;
      return normaliseBounds(vs?.mode, vs?.min, vs?.max, vs?.increment, vs?.listValues);
    }
    if (p.kind === "polar") {
      // Polar shows up as a compound in the Inspector — bounds are
      // applied per sub-field at render time (not here).
      return undefined;
    }
  }
  return undefined;
}

function normaliseBounds(
  mode: "none" | "increment" | "list" | undefined,
  min: number | undefined,
  max: number | undefined,
  increment: number | undefined,
  listValues: readonly number[] | undefined,
): DynNumericBounds | undefined {
  if (!mode && min === undefined && max === undefined) return undefined;
  const bounds: DynNumericBounds = { mode: mode ?? "none" };
  if (min !== undefined) bounds.min = min;
  if (max !== undefined) bounds.max = max;
  if (increment !== undefined && increment > 0) bounds.increment = increment;
  if (listValues && listValues.length > 0) bounds.listValues = listValues;
  return bounds;
}

/**
 * Classify `<name>.<suffix>` against the compound-kind map. Returns
 * undefined for names that are not suffixed or whose base is not a
 * compound parameter.
 */
function matchCompoundSuffix(
  name: string,
  kindMap: ReadonlyMap<string, CompoundKindAlias>,
): { base: string; kind: CompoundKindAlias; slot: "a" | "b" } | undefined {
  const dotIdx = name.lastIndexOf(".");
  if (dotIdx <= 0) return undefined;
  const base = name.slice(0, dotIdx);
  const suffix = name.slice(dotIdx);
  const kind = kindMap.get(base);
  if (!kind) return undefined;
  const [suffixA, suffixB] = compoundSuffixes(kind);
  if (suffix === suffixA) return { base, kind, slot: "a" };
  if (suffix === suffixB) return { base, kind, slot: "b" };
  return undefined;
}
